import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { metaTrack, productData } from "../analytics/metaPixel";

const ShopContext = createContext(null);

const load = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => load("rvi_cart", []));
  const [wishlist, setWishlist] = useState(() => load("rvi_wishlist", []));
  const [recent, setRecent] = useState(() => load("rvi_recent", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("rvi_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("rvi_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("rvi_recent", JSON.stringify(recent)), [recent]);

  const addToCart = (product, qty = 1, silent = false) => {
    metaTrack("AddToCart", productData(product));
    setCart((prev) => {
      const found = prev.find((i) => i.id === product.id);
      if (found) return prev.map((i) => (i.id === product.id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.images[0], qty }];
    });
    if (!silent) toast.success(`${product.name} added to your bag`);
  };

  const updateQty = (id, qty) => {
    if (qty < 1) return;
    setCart((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      if (prev.find((i) => i.id === product.id)) {
        toast.info(`${product.name} removed from wishlist`);
        return prev.filter((i) => i.id !== product.id);
      }
      toast.success(`${product.name} saved to wishlist`);
      return [...prev, { id: product.id, name: product.name, price: product.price, original_price: product.original_price, image: product.images[0], fabric: product.fabric }];
    });
  };

  const isWishlisted = (id) => wishlist.some((i) => i.id === id);

  const addRecentlyViewed = (product) => {
    setRecent((prev) => {
      const rest = prev.filter((i) => i.id !== product.id);
      return [{ id: product.id, name: product.name, price: product.price, image: product.images[0] }, ...rest].slice(0, 8);
    });
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <ShopContext.Provider
      value={{
        cart, cartCount, cartTotal, cartOpen, setCartOpen,
        addToCart, updateQty, removeFromCart, clearCart,
        wishlist, toggleWishlist, isWishlisted,
        recent, addRecentlyViewed,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export const useShop = () => useContext(ShopContext);
